/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
import 'swiper/less';
import 'swiper/less/navigation';
import 'swiper/less/pagination';
import 'swiper/less/scrollbar';
import './Swiper.less';

import { Icon } from '@coop/components';
import { useDebouncedCallback } from '@coop/hooks';
import { ChevronLeftIcon, ChevronRightIcon } from '@coop/icons';
import classNames from 'classnames';
import type { FC } from 'react';
import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { type SwiperClass, Swiper, SwiperSlide } from 'swiper/react';
import type { NavigationOptions, ScrollbarOptions, SwiperOptions } from 'swiper/types';

import { useA11yAnnouncerDispatch } from '../../hooks/useA11yAnnouncerDispatch';
import { useIsMobile } from '../atoms/ResponsiveBreakpoints';

type PixelValue = `${number}px`;

export interface SwipableListProps {
    swiperMainClassName?: string;
    swiperButtonClassName?: string;
    swiperPaginationClass?: string;
    swiperOptions?: SwiperOptions;
    onSwiper?: React.ComponentProps<typeof Swiper>['onSwiper'];
    swiperOverflowPx?: PixelValue;
    autoManageFocus?: boolean;
}

interface NavButtonProps {
    hasScrollbar: boolean;
    class?: string;
    totalPages: number;
    currentPage: number;
}

const PrevButton = React.forwardRef<HTMLButtonElement, NavButtonProps>((props, ref) => (
    <button
        ref={ref}
        type="button"
        className={classNames(
            'swiper-button swiper-button--prev',
            props.hasScrollbar && 'swiper-button--scrollbar',
            props.class,
        )}
        aria-label={`se föregående sida i listan, sida ${props.currentPage - 1} av ${props.totalPages}`}
    >
        <Icon icon={ChevronLeftIcon} className="swiper-arrow" aria-hidden />
    </button>
));

const NextButton = React.forwardRef<HTMLButtonElement, NavButtonProps>((props, ref) => (
    <button
        ref={ref}
        type="button"
        className={classNames(
            'swiper-button swiper-button--next',
            props.hasScrollbar && 'swiper-button--scrollbar',
            props.class,
        )}
        aria-label={`se nästa sida i listan, sida ${props.currentPage + 1} av ${props.totalPages}`}
    >
        <Icon icon={ChevronRightIcon} className="swiper-arrow" aria-hidden />
    </button>
));

const isScrollbarOptions = (
    scrollbar: boolean | ScrollbarOptions,
): scrollbar is ScrollbarOptions => {
    return (scrollbar as ScrollbarOptions).el !== undefined;
};

const modifySwiperOptions = (customSwiperOptions?: SwiperOptions): SwiperOptions => {
    let scrollbar: boolean | ScrollbarOptions = false;

    if (customSwiperOptions && customSwiperOptions.scrollbar) {
        if (!isScrollbarOptions(customSwiperOptions.scrollbar)) {
            scrollbar = {
                el: '.swiper-scrollbar-container',
                draggable: true,
            };
        } else {
            scrollbar = {
                el: '.swiper-scrollbar-container',
                draggable: true,
                ...customSwiperOptions.scrollbar,
            };
        }
    }

    return {
        watchSlidesProgress: true,
        ...customSwiperOptions,
        containerModifierClass: classNames('swiper-container--'),
        scrollbar,
    };
};

const SwipableList: FC<React.PropsWithChildren<SwipableListProps>> = (props) => {
    const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);
    const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
    const [paginationEl, setPaginationEl] = useState<HTMLDivElement | null>(null);

    const focusTimer = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const announce = useA11yAnnouncerDispatch();

    const { autoManageFocus = true } = props;

    // iphone crashes if we have too many items in the swiper
    const MAX_NUMBER_OF_SWIPABLE_ITEMS = 60;
    const FOCUS_TIMEOUT = 500;

    // this is important becase the ref wont change but the value in the ref will. So we need to use the element to trigger useMemo, not the ref
    const swiperOptions: SwiperOptions = useMemo(() => {
        return {
            threshold: 5,
            navigation: {
                ...(props.swiperOptions?.navigation as NavigationOptions),
                disabledClass: 'is-hidden',
                nextEl,
                prevEl,
            },
            pagination: {
                clickable: true,
                el: paginationEl,
                lockClass: 'is-hidden',
                dynamicBullets: true,
                dynamicMainBullets: 5,
            },
            ...modifySwiperOptions(props.swiperOptions),
        };
    }, [nextEl, prevEl, paginationEl, props.swiperOptions]);
    const isMobile = useIsMobile();

    const style: { [key: string]: string | undefined } = {
        '--swiperOverflowSpace': props.swiperOverflowPx,
    };

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);

    const setCurrentPageFromSnapIndex = (index: number) => {
        setCurrentPage(index + 1);
    };

    const handleSlidesLengthChange = useDebouncedCallback((swiper: SwiperClass) => {
        if (swiper.snapGrid) {
            setTotalPages(swiper.snapGrid.length);
        }
        setCurrentPageFromSnapIndex(swiper.snapIndex);
    }, 500);

    return (
        <>
            <div
                style={style}
                ref={containerRef}
                className={classNames(
                    'swiper',
                    props.swiperMainClassName,
                    isMobile && 'swiper-disabledNavigation',
                    !isMobile && swiperOptions?.watchSlidesProgress && 'swiper-watch',
                )}
            >
                <PrevButton
                    class={props.swiperButtonClassName}
                    ref={setPrevEl}
                    hasScrollbar={!!swiperOptions.scrollbar}
                    totalPages={totalPages}
                    currentPage={currentPage}
                />

                <Swiper
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...swiperOptions}
                    modules={[Navigation, Pagination, Scrollbar]}
                    onSwiper={props.onSwiper}
                    wrapperTag="ul"
                    // we can only trust lengthChange to properly set the total pages, otherwise onInit etc its not properly calculated yet
                    onSnapGridLengthChange={handleSlidesLengthChange}
                    onSlideChange={(swiper) => {
                        if (focusTimer.current) {
                            clearTimeout(focusTimer.current);
                        }

                        const afterSlideChange = () => {
                            setCurrentPageFromSnapIndex(swiper.snapIndex);

                            if (autoManageFocus) {
                                const focusElement =
                                    containerRef.current?.querySelector<HTMLElement>(
                                        '.swiper-slide-active a, .swiper-slide-active button',
                                    );
                                if (focusElement) {
                                    focusElement.focus();
                                }
                            }
                        };

                        announce({
                            text: `Visar sida ${swiper.snapIndex + 1} av ${swiper.snapGrid.length}`,
                            sender: 'SwipableList',
                        });
                        /** timeout much match transitiontime which is 400ms */
                        focusTimer.current = setTimeout(afterSlideChange, FOCUS_TIMEOUT);
                    }}
                >
                    {React.Children.map(
                        props.children,
                        (child, index) =>
                            child &&
                            index < MAX_NUMBER_OF_SWIPABLE_ITEMS && (
                                <SwiperSlide tag="li">{child}</SwiperSlide>
                            ),
                    )}
                    {swiperOptions.scrollbar && <div className="swiper-scrollbar-container" />}
                </Swiper>

                <NextButton
                    class={props.swiperButtonClassName}
                    ref={setNextEl}
                    hasScrollbar={!!swiperOptions.scrollbar}
                    totalPages={totalPages}
                    currentPage={currentPage}
                />
            </div>
            {swiperOptions.pagination && (
                <div
                    ref={setPaginationEl}
                    className={classNames('swiper-pagination', props.swiperPaginationClass)}
                />
            )}
        </>
    );
};

export default SwipableList;
